/**
 * ROI Models admin page (SmartImprove) — Feb 2026.
 *
 * Consulting-grade ROI Assumption Engine: lightweight benchmark library
 * for warehouse transformation business cases. Admin sets default
 * benchmarks; the consultant dashboard overrides per-engagement values.
 *
 * Feb-2026 governance pass (UI + metadata only — NO calculation changes):
 *   - Field-level info tooltips on every consulting benchmark driver.
 *   - Benchmark Source metadata (PrimeS4 / APQC / Gartner / Industry /
 *     Customer / Internal / Custom).
 *   - Benchmark Maturity positioning (Conservative → Lights-Out Vision).
 *   - Card-level metadata chips + multi-axis filtering (Process / Theme /
 *     Source / Maturity).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  Plus,
  Pencil,
  Trash2,
  TrendingUp,
  Package,
  Cpu,
  Wallet,
  Briefcase,
  Target,
  ChevronDown,
  ChevronRight,
  Info,
} from "lucide-react";
import {
  roiModelsApi,
  operationalAreasApi,
} from "../../lib/smartimproveAdminApi";

const TRANSFORMATION_THEMES = [
  "RF Modernization",
  "Warehouse Standardization",
  "Process Harmonization",
  "Inventory Optimization",
  "Workforce Productivity",
  "Automation Enablement",
  "KPI & Visibility Improvements",
  "Digital Transformation",
  "Operational Stability",
  "Integration Reliability",
  "Scalability Readiness",
];

// Benchmark governance metadata (UI-only — no calculation impact).
// Structured as { value, label } so the taxonomy can be extended without
// touching consumer components.
const BENCHMARK_SOURCES = [
  { value: "primes4", label: "PrimeS4 Benchmark" },
  { value: "customer_historical", label: "Customer Historical" },
  { value: "industry_standard", label: "Industry Standard" },
  { value: "apqc", label: "APQC Benchmark" },
  { value: "gartner", label: "Gartner Benchmark" },
  { value: "internal_program", label: "Internal Program Benchmark" },
  { value: "custom_consultant", label: "Custom Consultant Estimate" },
];

const BENCHMARK_MATURITY = [
  {
    value: "conservative",
    label: "Conservative",
    chip: "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]",
  },
  {
    value: "industry_average",
    label: "Industry Average",
    chip: "bg-[#EEF2F5] text-[#475569] border-[#CBD5E1]",
  },
  {
    value: "mature_warehouse",
    label: "Mature Warehouse",
    chip: "bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]",
  },
  {
    value: "best_in_class",
    label: "Best-in-Class",
    chip: "bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]",
  },
  {
    value: "highly_automated",
    label: "Highly Automated Site",
    chip: "bg-[#CCFBF1] text-[#0F766E] border-[#99F6E4]",
  },
  {
    value: "ai_assisted",
    label: "AI-Assisted Warehouse",
    chip: "bg-[#CFFAFE] text-[#0E7490] border-[#A5F3FC]",
  },
  {
    value: "lights_out",
    label: "Lights-Out Vision",
    chip: "bg-[#1F2937] text-[#E5E7EB] border-[#111827]",
  },
];

// Concise consulting-style help copy. Drives the field-level info icons
// across both the dialog sections and (later) consultant override pages.
const FIELD_HELP = {
  // Overview
  estimated_roi_low_pct:
    "Low end of the expected ROI band before realization confidence is applied.",
  estimated_roi_high_pct:
    "High end of the expected ROI band before realization confidence is applied.",
  primary_value_driver:
    "Headline benefit category that anchors the executive narrative for this benchmark.",
  benchmark_source:
    "Origin of the benchmark — used for governance, defensibility, and consulting traceability. Choose 'Custom Consultant Estimate' when no formal benchmark exists.",
  benchmark_maturity:
    "Warehouse maturity positioning the benchmark assumes — calibrates how aggressive the upside should be considered.",
  // Productivity Drivers
  inbound_productivity_pct:
    "Estimated productivity uplift across receiving, putaway and inbound staging.",
  outbound_productivity_pct:
    "Estimated productivity uplift across picking, packing and outbound staging.",
  replenishment_efficiency_pct:
    "Efficiency uplift from improved replenishment triggers, paths and slotting alignment.",
  labor_reduction_pct:
    "Net reduction in direct labour hours after productivity uplift is realized.",
  travel_time_reduction_pct:
    "Reduction in operator travel time from improved slotting, paths and task sequencing.",
  // Inventory & Accuracy
  inventory_accuracy_pct:
    "Expected uplift in inventory record accuracy after process and system improvements.",
  stock_discrepancy_reduction_pct:
    "Reduction in stock discrepancies identified by cycle counts and audits.",
  cycle_count_efficiency_pct:
    "Estimated reduction in effort/cost from a leaner, risk-based cycle count program.",
  inventory_carrying_cost_reduction_pct:
    "Reduction in inventory carrying cost from accuracy, slotting and turn improvements.",
  // Automation & Technology
  rf_productivity_uplift_pct:
    "Operator productivity uplift directly attributable to RF / mobile execution modernization.",
  automation_throughput_pct:
    "Throughput uplift from re-orchestrating installed automation with the WMS.",
  support_ticket_reduction_pct:
    "Reduction in production support tickets from stabilization and standardization.",
  training_effort_reduction_pct:
    "Reduction in onboarding and refresher training effort from standardized processes.",
  paper_process_elimination_pct:
    "Estimated reduction in manual paper-based warehouse activities after digitalization.",
  // Operational Cost Drivers
  avg_labor_cost_per_hr_usd:
    "Blended fully-loaded warehouse labour rate used to monetise productivity benefits.",
  cost_per_shipping_error_usd:
    "All-in cost per outbound shipping error (rework, freight, customer credits).",
  cost_per_inventory_discrepancy_usd:
    "All-in cost per inventory discrepancy event (investigation, write-off, lost sales).",
  downtime_cost_per_hr_usd:
    "Operational cost of one hour of unplanned warehouse / automation downtime.",
  warehouse_overhead_pct:
    "Indirect overhead applied on top of direct labour cost (supervision, facilities, IT).",
  // Investment Assumptions
  implementation_cost_min_usd:
    "Lower-bound one-time implementation investment for the indicative program.",
  implementation_cost_max_usd:
    "Upper-bound one-time implementation investment for the indicative program.",
  support_ams_pct:
    "Annual application management / support cost as a percentage of implementation cost.",
  infrastructure_cost_usd:
    "Indicative infrastructure / device / hardware spend required for the program.",
  training_change_cost_usd:
    "Indicative change-management, training and adoption spend across the program.",
  // Realization Confidence
  conservative_realization_pct:
    "Lower-bound realization expectation — used for defensible conservative business cases.",
  expected_realization_pct:
    "Most-likely realization expectation — the consulting-recommended central scenario.",
  optimistic_realization_pct:
    "Upper-bound realization expectation — used for stretch / aspirational business cases.",
  confidence_multiplier:
    "Used to reduce overly optimistic ROI projections based on execution risk and operational readiness.",
};

// Section -> [{key, label, suffix?}] — drives both dialog and card.
const SECTIONS = [
  {
    id: "productivity",
    icon: TrendingUp,
    title: "Productivity Drivers",
    fields: [
      { key: "inbound_productivity_pct", label: "Inbound productivity", suffix: "%" },
      { key: "outbound_productivity_pct", label: "Outbound productivity", suffix: "%" },
      { key: "replenishment_efficiency_pct", label: "Replenishment efficiency", suffix: "%" },
      { key: "labor_reduction_pct", label: "Labor reduction", suffix: "%" },
      { key: "travel_time_reduction_pct", label: "Travel-time reduction", suffix: "%" },
    ],
  },
  {
    id: "inventory",
    icon: Package,
    title: "Inventory & Accuracy",
    fields: [
      { key: "inventory_accuracy_pct", label: "Inventory accuracy", suffix: "%" },
      { key: "stock_discrepancy_reduction_pct", label: "Stock discrepancy reduction", suffix: "%" },
      { key: "cycle_count_efficiency_pct", label: "Cycle count efficiency", suffix: "%" },
      { key: "inventory_carrying_cost_reduction_pct", label: "Carrying cost reduction", suffix: "%" },
    ],
  },
  {
    id: "automation",
    icon: Cpu,
    title: "Automation & Technology",
    fields: [
      { key: "rf_productivity_uplift_pct", label: "RF productivity uplift", suffix: "%" },
      { key: "automation_throughput_pct", label: "Automation throughput", suffix: "%" },
      { key: "support_ticket_reduction_pct", label: "Support ticket reduction", suffix: "%" },
      { key: "training_effort_reduction_pct", label: "Training effort reduction", suffix: "%" },
      { key: "paper_process_elimination_pct", label: "Paper-process elimination", suffix: "%" },
    ],
  },
  {
    id: "cost",
    icon: Wallet,
    title: "Operational Cost Drivers",
    fields: [
      { key: "avg_labor_cost_per_hr_usd", label: "Avg. labor cost", suffix: "$/hr" },
      { key: "cost_per_shipping_error_usd", label: "Cost per shipping error", suffix: "$" },
      { key: "cost_per_inventory_discrepancy_usd", label: "Cost per inventory discrepancy", suffix: "$" },
      { key: "downtime_cost_per_hr_usd", label: "Downtime cost", suffix: "$/hr" },
      { key: "warehouse_overhead_pct", label: "Warehouse overhead", suffix: "%" },
    ],
  },
  {
    id: "investment",
    icon: Briefcase,
    title: "Investment Assumptions",
    fields: [
      { key: "implementation_cost_min_usd", label: "Implementation cost — min", suffix: "$" },
      { key: "implementation_cost_max_usd", label: "Implementation cost — max", suffix: "$" },
      { key: "support_ams_pct", label: "Support / AMS", suffix: "%" },
      { key: "infrastructure_cost_usd", label: "Infrastructure / devices", suffix: "$" },
      { key: "training_change_cost_usd", label: "Training / change", suffix: "$" },
    ],
  },
  {
    id: "confidence",
    icon: Target,
    title: "Realization Confidence",
    fields: [
      { key: "conservative_realization_pct", label: "Conservative realization", suffix: "%" },
      { key: "expected_realization_pct", label: "Expected realization", suffix: "%" },
      { key: "optimistic_realization_pct", label: "Optimistic realization", suffix: "%" },
      { key: "confidence_multiplier", label: "Confidence multiplier", suffix: "×" },
    ],
  },
];

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  name: "",
  description: "",
  operational_area: "",
  transformation_theme: "",
  primary_value_driver: "",
  // Sensible defaults so new ROI Models never land blank — admin can change.
  benchmark_source: "primes4",
  benchmark_maturity: "industry_average",
  recommendation_mapping: [],
  estimated_roi_low_pct: "",
  estimated_roi_high_pct: "",
  active: true,
  sort_order: 0,
};
// Populate empty for every section field.
SECTIONS.forEach((s) => s.fields.forEach((f) => (EMPTY[f.key] = "")));

const SOURCE_LABEL_BY_VALUE = Object.fromEntries(
  BENCHMARK_SOURCES.map((s) => [s.value, s.label])
);
const MATURITY_BY_VALUE = Object.fromEntries(
  BENCHMARK_MATURITY.map((m) => [m.value, m])
);

/**
 * InfoHint — small, reusable, enterprise-style help icon with a tooltip.
 * Click works on touch devices (Radix tooltip handles hover/focus). Kept
 * deliberately tiny to avoid layout shift inside dense benchmark grids.
 */
function InfoHint({ text, testId }) {
  if (!text) return null;
  return (
    <Tooltip delayDuration={120}>
      <TooltipTrigger asChild>
        <button
          type="button"
          tabIndex={0}
          aria-label="More info"
          data-testid={testId}
          onClick={(e) => e.preventDefault()}
          className="inline-flex items-center justify-center text-[#94A3B8] hover:text-[#025979] focus:outline-none focus:text-[#025979] align-middle"
        >
          <Info className="w-3 h-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="start"
        className="max-w-[260px] bg-[#0F172A] text-[#E2E8F0] text-[11px] leading-[1.45] font-normal rounded-sm px-2.5 py-2 shadow-md border border-[#1F2937]"
      >
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

function FieldLabel({ children, helpKey, suffix }) {
  return (
    <label className="flex items-center gap-1.5 text-xs font-medium text-[#374151] mb-1">
      <span>{children}</span>
      {suffix ? <span className="text-[#9CA3AF]">{suffix}</span> : null}
      <InfoHint text={FIELD_HELP[helpKey]} testId={`roi-help-${helpKey}`} />
    </label>
  );
}

function Section({ icon: Icon, title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E7EB] rounded-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F8FAFC]"
      >
        <span className="flex items-center gap-2">
          {Icon ? <Icon className="w-3.5 h-3.5 text-[#025979]" /> : null}
          <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            {title}
          </span>
        </span>
        {open ? (
          <ChevronDown className="w-3.5 h-3.5 text-[#6B7280]" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 text-[#6B7280]" />
        )}
      </button>
      {open ? (
        <div className="px-4 pt-1 pb-4 border-t border-[#F1F5F9]">{children}</div>
      ) : null}
    </div>
  );
}

function fmtCompact(n) {
  if (n === null || n === undefined || n === "") return null;
  const v = Number(n);
  if (!Number.isFinite(v)) return null;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(0)}k`;
  return `$${Math.round(v)}`;
}

function RoiDialog({ open, onOpenChange, value, processes, onChange, onSave, saving }) {
  const isEdit = !!value?.id;
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="roi-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit ROI Benchmark Model" : "New ROI Benchmark Model"}</DialogTitle>
          <DialogDescription>
            Default consulting benchmarks. Consultants override per-engagement values in the dashboard.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 max-h-[72vh] overflow-y-auto pr-1">
          <Section icon={Target} title="Overview" defaultOpen>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">ROI Model Name</label>
                <Input
                  value={value?.name || ""}
                  onChange={(e) => set({ name: e.target.value })}
                  placeholder="e.g. RF Modernization Benchmark"
                  className="rounded-sm text-xs"
                  data-testid="roi-field-name"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Business Process</label>
                <Select
                  value={value?.operational_area || ""}
                  onValueChange={(v) => set({ operational_area: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="roi-field-process">
                    <SelectValue placeholder="Select business process" />
                  </SelectTrigger>
                  <SelectContent>
                    {processes
                      .filter((p) => p.active !== false)
                      .map((p) => (
                        <SelectItem key={p.key} value={p.key}>
                          {p.label}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#374151] mb-1">Transformation Theme</label>
                <Select
                  value={value?.transformation_theme || ""}
                  onValueChange={(v) => set({ transformation_theme: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="roi-field-theme">
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSFORMATION_THEMES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel helpKey="benchmark_maturity">Benchmark Maturity</FieldLabel>
                <Select
                  value={value?.benchmark_maturity || ""}
                  onValueChange={(v) => set({ benchmark_maturity: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="roi-field-maturity">
                    <SelectValue placeholder="Select maturity" />
                  </SelectTrigger>
                  <SelectContent>
                    {BENCHMARK_MATURITY.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel helpKey="benchmark_source">Benchmark Source Type</FieldLabel>
                <Select
                  value={value?.benchmark_source || ""}
                  onValueChange={(v) => set({ benchmark_source: v })}
                >
                  <SelectTrigger className="rounded-sm text-xs" data-testid="roi-field-source">
                    <SelectValue placeholder="Select source type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BENCHMARK_SOURCES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel helpKey="primary_value_driver">Primary Value Driver</FieldLabel>
                <Input
                  value={value?.primary_value_driver || ""}
                  onChange={(e) => set({ primary_value_driver: e.target.value })}
                  placeholder="e.g. Workforce Productivity"
                  className="rounded-sm text-xs"
                  data-testid="roi-field-driver"
                />
              </div>
              <div>
                <FieldLabel helpKey="estimated_roi_low_pct" suffix="(%)">
                  Estimated ROI low
                </FieldLabel>
                <Input
                  type="number"
                  value={value?.estimated_roi_low_pct ?? ""}
                  onChange={(e) => set({ estimated_roi_low_pct: e.target.value })}
                  className="rounded-sm text-xs"
                  data-testid="roi-field-roi-low"
                />
              </div>
              <div>
                <FieldLabel helpKey="estimated_roi_high_pct" suffix="(%)">
                  Estimated ROI high
                </FieldLabel>
                <Input
                  type="number"
                  value={value?.estimated_roi_high_pct ?? ""}
                  onChange={(e) => set({ estimated_roi_high_pct: e.target.value })}
                  className="rounded-sm text-xs"
                  data-testid="roi-field-roi-high"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-[#374151] mb-1">Description</label>
                <Textarea
                  value={value?.description || ""}
                  onChange={(e) => set({ description: e.target.value })}
                  rows={2}
                  className="rounded-sm text-xs"
                  data-testid="roi-field-description"
                />
              </div>
            </div>
          </Section>

          {SECTIONS.map((sec) => (
            <Section key={sec.id} icon={sec.icon} title={sec.title}>
              <div className="grid grid-cols-3 gap-3">
                {sec.fields.map((f) => (
                  <div key={f.key}>
                    <FieldLabel helpKey={f.key} suffix={f.suffix}>
                      {f.label}
                    </FieldLabel>
                    <Input
                      type="number"
                      step="0.01"
                      value={value?.[f.key] ?? ""}
                      onChange={(e) => set({ [f.key]: e.target.value })}
                      className="rounded-sm text-xs"
                      data-testid={`roi-field-${f.key}`}
                    />
                  </div>
                ))}
              </div>
            </Section>
          ))}

          <Section title="Meta">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  id="roi-active"
                  type="checkbox"
                  checked={value?.active !== false}
                  onChange={(e) => set({ active: e.target.checked })}
                  data-testid="roi-field-active"
                />
                <label htmlFor="roi-active" className="text-xs text-[#374151]">
                  Active
                </label>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-[#374151]">Sort order</label>
                <Input
                  type="number"
                  value={value?.sort_order ?? 0}
                  onChange={(e) => set({ sort_order: e.target.value })}
                  className="rounded-sm text-xs w-24"
                  data-testid="roi-field-sort-order"
                />
              </div>
            </div>
          </Section>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm text-xs">
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !value?.name?.trim()}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="roi-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MaturityChip({ value }) {
  const m = MATURITY_BY_VALUE[value];
  if (!m) return null;
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm ${m.chip}`}
      data-testid={`roi-card-maturity-${value}`}
    >
      {m.label}
    </span>
  );
}

function RoiCard({ row, processByKey, onEdit, onRemove }) {
  const bp = processByKey[row.operational_area];
  const roiBand =
    row.estimated_roi_low_pct != null && row.estimated_roi_high_pct != null
      ? `${row.estimated_roi_low_pct}–${row.estimated_roi_high_pct}%`
      : null;
  const exp = row.expected_realization_pct;
  const investLow = fmtCompact(row.implementation_cost_min_usd ?? row.implementation_cost_min);
  const investHigh = fmtCompact(row.implementation_cost_max_usd ?? row.implementation_cost_max);
  const invest = investLow && investHigh ? `${investLow}–${investHigh}` : null;
  const sourceLabel = SOURCE_LABEL_BY_VALUE[row.benchmark_source];
  return (
    <div
      className={`bg-white border border-[#E5E7EB] rounded-sm p-5 hover:border-[#94A3B8] transition ${
        row.active === false ? "opacity-60" : ""
      }`}
      data-testid={`roi-card-${row.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {row.transformation_theme ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]">
                {row.transformation_theme}
              </span>
            ) : null}
            <MaturityChip value={row.benchmark_maturity} />
            {row.active === false ? (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                inactive
              </span>
            ) : null}
          </div>
          <h3
            className="text-base font-bold text-[#111827]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {row.name}
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-1">
            {bp?.label || "—"}
            {row.primary_value_driver ? ` · ${row.primary_value_driver}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <button
            onClick={() => onEdit(row)}
            className="text-xs font-semibold text-[#025979] hover:text-[#03738C]"
            data-testid={`roi-edit-${row.id}`}
          >
            <Pencil className="inline w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => onRemove(row)}
            className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
            data-testid={`roi-delete-${row.id}`}
          >
            <Trash2 className="inline w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {row.description ? (
        <p className="text-xs text-[#374151] mb-3 line-clamp-2">{row.description}</p>
      ) : null}
      <div className="grid grid-cols-3 gap-3 text-[11px]">
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Estimated ROI</p>
          <p className="text-[#111827] font-semibold">{roiBand || "—"}</p>
        </div>
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Realization</p>
          <p className="text-[#111827] font-semibold">{exp != null ? `${exp}%` : "—"}</p>
        </div>
        <div>
          <p className="text-[#6B7280] uppercase tracking-wider mb-0.5">Investment</p>
          <p className="text-[#111827] font-semibold">{invest || "—"}</p>
        </div>
      </div>
      {(row.recommendation_mapping || []).length > 0 ? (
        <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-wider mb-1.5">
            Linked Recommendations
          </p>
          <div className="flex flex-wrap gap-1">
            {(row.recommendation_mapping || []).slice(0, 4).map((s) => (
              <span
                key={s}
                className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151]"
              >
                {s}
              </span>
            ))}
            {(row.recommendation_mapping || []).length > 4 ? (
              <span className="px-1.5 py-0.5 text-[10px] text-[#6B7280]">
                +{(row.recommendation_mapping || []).length - 4} more
              </span>
            ) : null}
          </div>
        </div>
      ) : null}
      {sourceLabel || row.benchmark_maturity ? null : null}
      <div
        className="mt-3 pt-2 border-t border-[#F1F5F9] grid grid-cols-2 gap-3"
        data-testid={`roi-card-meta-${row.id}`}
      >
        <div data-testid={`roi-card-source-${row.id}`}>
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-[0.14em] mb-0.5">
            Source
          </p>
          <p className="text-[11px] font-medium text-[#475569]">
            {sourceLabel || (
              <span className="text-[#94A3B8] italic">Not specified</span>
            )}
          </p>
        </div>
        <div data-testid={`roi-card-maturity-meta-${row.id}`}>
          <p className="text-[10px] text-[#94A3B8] uppercase tracking-[0.14em] mb-0.5">
            Maturity
          </p>
          <p className="text-[11px] font-medium text-[#475569]">
            {MATURITY_BY_VALUE[row.benchmark_maturity]?.label || (
              <span className="text-[#94A3B8] italic">Not specified</span>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function RoiModelsPage() {
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterProcess, setFilterProcess] = useState("__all");
  const [filterTheme, setFilterTheme] = useState("__all");
  const [filterSource, setFilterSource] = useState("__all");
  const [filterMaturity, setFilterMaturity] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [items, bps] = await Promise.all([
        roiModelsApi.list(),
        operationalAreasApi.list(),
      ]);
      setRows(items || []);
      setProcesses(bps || []);
    } catch (e) {
      toast.error("Failed to load ROI models");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const processByKey = useMemo(() => {
    const m = {};
    processes.forEach((p) => (m[p.key] = p));
    return m;
  }, [processes]);

  const sortedRows = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (filterProcess !== "__all" && r.operational_area !== filterProcess)
        return false;
      if (filterTheme !== "__all" && r.transformation_theme !== filterTheme)
        return false;
      if (filterSource !== "__all" && (r.benchmark_source || "") !== filterSource)
        return false;
      if (
        filterMaturity !== "__all" &&
        (r.benchmark_maturity || "") !== filterMaturity
      )
        return false;
      return true;
    });
    return [...filtered].sort(
      (a, b) =>
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.name).localeCompare(String(b.name))
    );
  }, [rows, filterProcess, filterTheme, filterSource, filterMaturity]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    const next = { ...EMPTY };
    Object.keys(row).forEach((k) => {
      next[k] = row[k] ?? next[k] ?? "";
    });
    next.active = row.active !== false;
    next.sort_order = row.sort_order ?? 0;
    next.recommendation_mapping = row.recommendation_mapping || [];
    setForm(next);
    setDialog(true);
  };
  const toNumOrNull = (v) =>
    v === "" || v === null || v === undefined
      ? null
      : Number.isNaN(Number(v))
      ? v
      : Number(v);

  const save = async () => {
    const isEdit = !!form.id;
    const name = (form.name || "").trim();
    if (!name) return;
    const slug = isEdit ? form.slug : slugify(name);
    const numericKeys = [
      "estimated_roi_low_pct",
      "estimated_roi_high_pct",
      ...SECTIONS.flatMap((s) => s.fields.map((f) => f.key)),
    ];
    const payload = {
      slug,
      name,
      description: (form.description || "").trim(),
      operational_area: form.operational_area || "",
      transformation_theme: form.transformation_theme || "",
      primary_value_driver: (form.primary_value_driver || "").trim(),
      benchmark_source: form.benchmark_source || "",
      benchmark_maturity: form.benchmark_maturity || "",
      recommendation_mapping: form.recommendation_mapping || [],
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    numericKeys.forEach((k) => {
      payload[k] = toNumOrNull(form[k]);
    });
    setSaving(true);
    try {
      if (isEdit) await roiModelsApi.update(form.id, payload);
      else await roiModelsApi.create(payload);
      toast.success(`ROI model ${isEdit ? "updated" : "created"}`);
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await roiModelsApi.remove(row.id);
      toast.success("ROI model deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const themeOptions = useMemo(() => {
    const present = new Set(
      rows.map((r) => r.transformation_theme).filter(Boolean)
    );
    return TRANSFORMATION_THEMES.filter((t) => present.has(t));
  }, [rows]);

  return (
    <TooltipProvider delayDuration={120}>
      <div className="space-y-5" data-testid="si-roi-models">
        <header className="flex items-start justify-between">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
              SmartImprove · ROI Assumption Engine
            </p>
            <h1
              className="text-2xl font-bold text-[#111827] mt-1"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              ROI Models
            </h1>
            <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
              Consulting-grade ROI benchmark models. Admin defines default
              benchmark assumptions; the consultant dashboard overrides
              per-engagement values when finalizing the business case.
            </p>
          </div>
          <Button
            onClick={openCreate}
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="roi-add-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add ROI Model
          </Button>
        </header>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#6B7280] mr-1">
            Filters
          </span>
          <Select value={filterProcess} onValueChange={setFilterProcess}>
            <SelectTrigger
              className="rounded-sm text-xs w-56"
              data-testid="roi-filter-process"
            >
              <SelectValue placeholder="Business Process" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All Business Processes</SelectItem>
              {processes
                .filter((p) => p.active !== false)
                .map((p) => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select value={filterTheme} onValueChange={setFilterTheme}>
            <SelectTrigger
              className="rounded-sm text-xs w-56"
              data-testid="roi-filter-theme"
            >
              <SelectValue placeholder="Transformation Theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All Themes</SelectItem>
              {themeOptions.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterSource} onValueChange={setFilterSource}>
            <SelectTrigger
              className="rounded-sm text-xs w-56"
              data-testid="roi-filter-source"
            >
              <SelectValue placeholder="Benchmark Source Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All Source Types</SelectItem>
              {BENCHMARK_SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterMaturity} onValueChange={setFilterMaturity}>
            <SelectTrigger
              className="rounded-sm text-xs w-56"
              data-testid="roi-filter-maturity"
            >
              <SelectValue placeholder="Benchmark Maturity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all">All Maturity Levels</SelectItem>
              {BENCHMARK_MATURITY.map((m) => (
                <SelectItem key={m.value} value={m.value}>
                  {m.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-[#6B7280] ml-auto">
            {sortedRows.length} model{sortedRows.length === 1 ? "" : "s"}
          </span>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div
            className="bg-white border border-[#E5E7EB] rounded-sm px-5 py-12 text-center text-sm text-[#6B7280]"
            data-testid="roi-empty"
          >
            No ROI models match the current filters. Click{" "}
            <strong>Add ROI Model</strong> to create one.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sortedRows.map((row) => (
              <RoiCard
                key={row.id}
                row={row}
                processByKey={processByKey}
                onEdit={openEdit}
                onRemove={remove}
              />
            ))}
          </div>
        )}

        <RoiDialog
          open={dialog}
          onOpenChange={setDialog}
          value={form}
          processes={processes}
          onChange={setForm}
          onSave={save}
          saving={saving}
        />
      </div>
    </TooltipProvider>
  );
}
